// src/components/TeamScreen.jsx
import { useState, useEffect } from "react";
import { ROLES, CATEGORIES } from "../lib/roles";
import { PERMISSION_LEVELS, canManageTeam } from "../lib/permissions";
import { createInvite, getInvites, cancelInvite, updateMember, removeMember } from "../lib/db";

const inputStyle = {
  width:"100%", background:"#0F172A", border:"1px solid #2D3748",
  color:"#F1F5F9", padding:"9px 12px", borderRadius:8, fontSize:14,
  outline:"none", fontFamily:"inherit", boxSizing:"border-box"
};
const labelStyle = {
  display:"block", fontSize:11, fontWeight:700, color:"#64748B",
  textTransform:"uppercase", letterSpacing:"0.1em", marginBottom:5
};

export function TeamScreen({ currentMember, members, onClose, showToast }) {
  const [invites, setInvites] = useState([]);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email:"", permission:"staff", allowedRoles:["pcc"] });
  const [loading, setLoading] = useState(false);
  const [editingRolesFor, setEditingRolesFor] = useState(null); // uid being edited

  const isOwner = currentMember?.permission === "owner";
  const canManage = canManageTeam(currentMember);

  useEffect(() => {
    if (canManage) getInvites().then(setInvites);
  }, [canManage]);

  const handleInvite = async () => {
    if (!inviteForm.email) return;
    setLoading(true);
    try {
      const inviteId = await createInvite(
        inviteForm.email, inviteForm.permission,
        inviteForm.allowedRoles, currentMember.uid
      );
      // In production: send email via Cloud Function with link
      // For now: show the join URL
      const joinUrl = `${window.location.origin}?invite=${inviteId}`;
      await navigator.clipboard.writeText(joinUrl);
      showToast("Invite link copied to clipboard — paste it and send!", "info");
      setInvites(prev => [...prev, { id: inviteId, ...inviteForm, status:"pending" }]);
      setShowInviteForm(false);
      setInviteForm({ email:"", permission:"staff", allowedRoles:["pcc"] });
    } catch(e) {
      showToast(e.message || "Invite failed", "error");
    }
    setLoading(false);
  };

  const handleCancelInvite = async (id) => {
    await cancelInvite(id);
    setInvites(prev => prev.filter(i => i.id !== id));
    showToast("Invite cancelled", "info");
  };

  const handlePermissionChange = async (uid, permission) => {
    const updates = { permission };
    // Lock external members to pyha-only
    if (permission === "external") updates.allowedRoles = ["pyha"];
    await updateMember(uid, updates);
    showToast("Permission updated");
  };

  const handleToggleMemberRole = async (uid, roleId, currentRoles) => {
    if (currentRoles.length === 1 && currentRoles.includes(roleId)) return; // keep at least one
    const next = currentRoles.includes(roleId)
      ? currentRoles.filter(r => r !== roleId)
      : [...currentRoles, roleId];
    await updateMember(uid, { allowedRoles: next });
    showToast("Role access updated");
  };

  const handleRemove = async (uid, name) => {
    if (!window.confirm(`Remove ${name} from the team?`)) return;
    await removeMember(uid);
    showToast(`${name} removed`, "info");
  };

  const toggleRole = (roleId) => {
    if (inviteForm.permission === "external") return; // locked to pyha
    setInviteForm(f => ({
      ...f,
      allowedRoles: f.allowedRoles.includes(roleId)
        ? f.allowedRoles.filter(r => r !== roleId)
        : [...f.allowedRoles, roleId]
    }));
  };

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.8)", zIndex:300, overflowY:"auto", paddingTop:"env(safe-area-inset-top)" }}>
      <div style={{ maxWidth:500, margin:"0 auto", padding:"20px 16px 100px" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
          <div style={{ fontSize:18, fontWeight:800, color:"#F1F5F9" }}>Team</div>
          <button onClick={onClose} style={{ background:"transparent", border:"none", color:"#64748B", cursor:"pointer", fontSize:20 }}>✕</button>
        </div>

        {/* Current members */}
        <div style={{ marginBottom:24 }}>
          <div style={{ fontSize:11, fontWeight:700, color:"#64748B", textTransform:"uppercase", letterSpacing:"0.1em", marginBottom:12 }}>
            Members ({members.length})
          </div>
          {members.map(m => (
            <div key={m.uid} style={{ background:"#1E293B", border:"1px solid #2D3748", borderRadius:10, padding:"12px 14px", marginBottom:8 }}>
              <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                {m.photoURL
                  ? <img src={m.photoURL} alt="" style={{ width:36, height:36, borderRadius:"50%" }} />
                  : <div style={{ width:36, height:36, borderRadius:"50%", background:"#2D3748", display:"flex", alignItems:"center", justifyContent:"center", color:"#94A3B8", fontWeight:700, fontSize:14 }}>{(m.displayName||m.email)[0].toUpperCase()}</div>
                }
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:14, fontWeight:700, color:"#F1F5F9" }}>{m.displayName || m.email}</div>
                  <div style={{ fontSize:11, color:"#64748B" }}>{m.email}</div>
                </div>
                {canManage && m.permission !== "owner" && m.uid !== currentMember.uid && (
                  <select value={m.permission}
                    onChange={e => handlePermissionChange(m.uid, e.target.value)}
                    style={{ background:"#0F172A", border:"1px solid #2D3748", color:"#94A3B8", padding:"4px 8px", borderRadius:6, fontSize:12, cursor:"pointer" }}>
                    {Object.entries(PERMISSION_LEVELS).filter(([k]) => k !== "owner").map(([k,v]) => (
                      <option key={k} value={k}>{v.label}</option>
                    ))}
                  </select>
                )}
                {m.permission !== "owner" && <span style={{ fontSize:11, padding:"2px 8px", borderRadius:8, background:"#2D3748", color:"#94A3B8" }}>{PERMISSION_LEVELS[m.permission]?.label}</span>}
                {isOwner && m.uid !== currentMember.uid && (
                  <button onClick={() => handleRemove(m.uid, m.displayName)}
                    style={{ background:"transparent", border:"none", color:"#EF4444", cursor:"pointer", fontSize:14, padding:"4px" }}>✕</button>
                )}
              </div>
              {/* Role access — shown for all non-owners; editable by owner */}
              {m.permission !== "owner" && m.allowedRoles && (
                <div style={{ marginTop:8 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:6, flexWrap:"wrap" }}>
                    <span style={{ fontSize:10, fontWeight:700, color:"#475569", textTransform:"uppercase", letterSpacing:"0.08em" }}>
                      Role access:
                    </span>
                    {m.permission === "external"
                      ? <span style={{ fontSize:10, padding:"2px 8px", borderRadius:8, background:"rgba(14,165,233,0.1)", color:"#0EA5E9", fontWeight:700 }}>PYHA only</span>
                      : m.allowedRoles.map(rId => {
                          const role = ROLES.find(r => r.id === rId);
                          return role ? (
                            <span key={rId} style={{ fontSize:10, padding:"2px 8px", borderRadius:8, background: role.accentBg, color: role.accent, fontWeight:700 }}>
                              {role.shortName}
                            </span>
                          ) : null;
                        })
                    }
                    {isOwner && m.uid !== currentMember.uid && m.permission !== "external" && (
                      <button onClick={() => setEditingRolesFor(editingRolesFor === m.uid ? null : m.uid)}
                        style={{ fontSize:10, color:"#475569", background:"transparent", border:"1px solid #2D3748", borderRadius:6, padding:"1px 7px", cursor:"pointer" }}>
                        {editingRolesFor === m.uid ? "Done" : "Edit"}
                      </button>
                    )}
                  </div>
                  {editingRolesFor === m.uid && (
                    <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginTop:6 }}>
                      {ROLES.map(role => {
                        const active = (m.allowedRoles || []).includes(role.id);
                        return (
                          <button key={role.id}
                            onClick={() => handleToggleMemberRole(m.uid, role.id, m.allowedRoles || [])}
                            style={{ background: active ? role.accentBg : "#0F172A", border:`1px solid ${active ? role.accent : "#2D3748"}`, color: active ? role.accent : "#64748B", padding:"4px 10px", borderRadius:8, fontSize:11, fontWeight:700, cursor:"pointer" }}>
                            {role.shortName}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Pending invites */}
        {invites.length > 0 && (
          <div style={{ marginBottom:24 }}>
            <div style={{ fontSize:11, fontWeight:700, color:"#64748B", textTransform:"uppercase", letterSpacing:"0.1em", marginBottom:12 }}>
              Pending invites
            </div>
            {invites.map(inv => (
              <div key={inv.id} style={{ background:"#1E293B", border:"1px solid #2D3748", borderRadius:10, padding:"10px 14px", marginBottom:6, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                <div>
                  <div style={{ fontSize:13, color:"#F1F5F9", fontWeight:600 }}>{inv.email}</div>
                  <div style={{ fontSize:11, color:"#64748B" }}>{PERMISSION_LEVELS[inv.permission]?.label} · {inv.allowedRoles?.join(", ")}</div>
                </div>
                <button onClick={() => handleCancelInvite(inv.id)}
                  style={{ background:"transparent", border:"none", color:"#EF4444", cursor:"pointer", fontSize:12 }}>Cancel</button>
              </div>
            ))}
          </div>
        )}

        {/* Invite form */}
        {canManage && (
          showInviteForm ? (
            <div style={{ background:"#1E293B", border:"1px solid #2D3748", borderRadius:12, padding:20 }}>
              <div style={{ fontSize:14, fontWeight:700, color:"#F1F5F9", marginBottom:16 }}>Invite team member</div>
              <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
                <div>
                  <label style={labelStyle}>Email address</label>
                  <input value={inviteForm.email} onChange={e => setInviteForm(f=>({...f,email:e.target.value}))}
                    placeholder="sandy@paradisecreek.com" style={inputStyle} type="email" />
                </div>
                <div>
                  <label style={labelStyle}>Permission level</label>
                  <select value={inviteForm.permission} onChange={e => {
                    const p = e.target.value;
                    setInviteForm(f => ({ ...f, permission: p, allowedRoles: p === "external" ? ["pyha"] : f.allowedRoles }));
                  }} style={inputStyle}>
                    {Object.entries(PERMISSION_LEVELS).filter(([k]) => k !== "owner").map(([k, v]) => (
                      <option key={k} value={k}>{v.label} — {
                        k === "manager" ? "sees all tasks in their roles, can assign" :
                        k === "staff"   ? "sees own tasks only in their roles" :
                                          "PYHA participants — own assigned tasks only"
                      }</option>
                    ))}
                  </select>
                </div>
                {inviteForm.permission === "external" && (
                  <div style={{ background:"rgba(14,165,233,0.08)", border:"1px solid rgba(14,165,233,0.25)", borderRadius:8, padding:"8px 12px", fontSize:12, color:"#7DD3FC" }}>
                    External users can only see PYHA tasks assigned to them. They cannot create, edit, or delete tasks, and cannot see other team members.
                  </div>
                )}
                {(inviteForm.permission === "staff" || inviteForm.permission === "manager") && (
                  <div>
                    <label style={labelStyle}>Role access</label>
                    <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                      {ROLES.map(role => (
                        <button key={role.id} onClick={() => toggleRole(role.id)}
                          style={{
                            background: inviteForm.allowedRoles.includes(role.id) ? role.accentBg : "#0F172A",
                            border: `1px solid ${inviteForm.allowedRoles.includes(role.id) ? role.accent : "#2D3748"}`,
                            color: inviteForm.allowedRoles.includes(role.id) ? role.accent : "#64748B",
                            padding:"6px 12px", borderRadius:8, fontSize:12, fontWeight:700, cursor:"pointer"
                          }}>{role.shortName}</button>
                      ))}
                    </div>
                    <div style={{ fontSize:11, color:"#475569", marginTop:6 }}>Staff can only see tasks in selected roles</div>
                  </div>
                )}
              </div>
              <div style={{ display:"flex", gap:10, marginTop:16 }}>
                <button onClick={() => setShowInviteForm(false)}
                  style={{ flex:1, background:"#2D3748", color:"#94A3B8", border:"none", padding:10, borderRadius:8, fontSize:13, cursor:"pointer" }}>
                  Cancel
                </button>
                <button onClick={handleInvite} disabled={loading || !inviteForm.email}
                  style={{ flex:2, background:"#2DD4BF", color:"#0F172A", border:"none", padding:10, borderRadius:8, fontSize:13, fontWeight:800, cursor:"pointer", opacity: loading ? 0.7 : 1 }}>
                  {loading ? "Creating…" : "Create invite link"}
                </button>
              </div>
            </div>
          ) : (
            <button onClick={() => setShowInviteForm(true)}
              style={{ width:"100%", background:"#1E293B", border:"1px dashed #2D3748", color:"#64748B", padding:14, borderRadius:10, fontSize:13, cursor:"pointer", fontWeight:600 }}>
              + Invite team member
            </button>
          )
        )}
      </div>
      {/* Sticky bottom close button for mobile */}
      <div style={{ position:"sticky", bottom:0, padding:"12px 16px", background:"rgba(15,23,42,0.95)",
        borderTop:"1px solid #2D3748", paddingBottom:"calc(12px + env(safe-area-inset-bottom))" }}>
        <button onClick={onClose} style={{ width:"100%", background:"#2D3748", border:"none",
          color:"#F1F5F9", padding:14, borderRadius:10, fontSize:15, fontWeight:700, cursor:"pointer" }}>
          Close
        </button>
      </div>
    </div>
  );
}
